<template>
  <global-modal name="event-modal-subexponent" v-if="event">
    <Modal @close="onCloseModal">
      <Popup
        v-if="!!subexponentIdToBeDeleted"
        @close="cancelDeleteSubexponentPopupAction"
        @confirm="deleteSubexponentPopupAction"
        remove
      >
        {{ $t("event.subexponent.modal.deleteAlert") }}
      </Popup>
      <Popup
        v-if="!!subexponentAllToBeDeleted"
        remove
        @close="cancelDeleteAllSubexponentPopupAction"
        @confirm="deleteAllsubParticipants"
      >
        {{ $t("event.subexponent.modal.deleteAllAlert") }}
      </Popup>
      <div class="subexponent">
        <div class="subexponent__header">
          <div class="subexponent__header-top subexponent_container">
            <h1 class="subexponent__header-top-title">
              {{ $t("event.subexponent.title") }}
            </h1>
            <p class="subexponent__header-top-all">
              {{
                $t("event.subexponent.modal.totalExhibitors", {
                  amount: subParticipants.length
                })
              }}

              {{
                subParticipants.length
                  | pluralize($t("event.subexponent.pluralize"))
              }}
            </p>
          </div>

          <div class="subexponent__header-quotas subexponent_container">
            <p class="subexponent__header-quotas-text">
              {{ $t("event.subexponent.modal.totalQuotas") }}
              <span>{{ quoteSubexpPass }}</span>
            </p>

            <p class="subexponent__header-quotas-text">
              {{ $t("event.subexponent.modal.availableQuotas1") }}

              <span class="subexponent__header-quotas-text_mobile">
                {{ $t("event.subexponent.modal.availableQuotas2") }}
              </span>

              <span>{{ availableQuotas }}</span>
            </p>
          </div>

          <div
            class="subexponent__header-add subexponent_container"
            v-if="hasAccessToWrite && !isAddFormOpen && subParticipants.length"
          >
            <LinkIcon
              icon="add"
              class="subexponent__header-add-btn"
              @click="isAddFormOpen = true"
            >
              {{ $t("event.subexponent.modal.addSubexponentTitle") }}
            </LinkIcon>

            <button
              class="subexponent__header-add-btn-delete"
              @click="showPopupDeleteAll"
              v-if="subParticipants.length"
            >
              {{ $t("event.subexponent.modal.deleteAll") }}
            </button>
          </div>

          <div
            class="subexponent__header-add-block"
            v-if="isAddFormOpen || !subParticipants.length"
          >
            <div class="subexponent__header-add-block_container">
              <h2 class="subexponent__header-add-block-title">
                {{ $t("event.subexponent.modal.addSubexponentTitle") }}
                <button
                  class="button-clean"
                  @click="isAddFormOpen = false"
                  v-if="subParticipants.length"
                >
                  <icon name="arrow-down_grey" />
                </button>
              </h2>
              <form @submit.prevent="addSubexponent">
                <div class="subexponent__header-add-block-inputs">
                  <InputLabel class="subexponent__header-add-block-inputs-item">
                    <template #label>
                      {{ $t("common.companyName") }}
                      <span class="subexponent-form__required">*</span>
                    </template>

                    <input type="text" v-model="addForm.companyName" />
                  </InputLabel>
                  <InputLabel class="subexponent__header-add-block-inputs-item">
                    <template #label>
                      E-mail
                      <span class="subexponent-form__required">*</span>
                    </template>

                    <input type="text" v-model="addForm.email" />
                  </InputLabel>
                  <InputLabel class="subexponent__header-add-block-inputs-item">
                    <template #label>
                      {{ $t("common.inn") }}
                      <span
                        v-if="locale === 'ru'"
                        class="subexponent-form__required"
                        >*</span
                      >
                    </template>
                    <input
                      v-if="locale === 'en'"
                      type="text"
                      v-model="addForm.inn"
                    />
                    <the-mask
                      v-else
                      mask="FFFFFFFFFFFFFF"
                      :tokens="hexTokens"
                      type="text"
                      v-model="addForm.inn"
                    />
                  </InputLabel>

                  <InputLabel
                    class="subexponent__header-add-block-inputs-item-number"
                  >
                    <template #label>
                      {{ $t("event.subexponent.modal.addQuotasLabel") }}
                      <span class="subexponent-form__required">*</span>
                    </template>
                    <template #label-right>
                      <tooltip clickable>
                        <button class="button-clean">
                          <icon name="info" />
                        </button>
                        <template #content>
                          <div class="description">
                            {{ $t("event.subexponent.modal.tooltip") }}
                          </div>
                        </template>
                      </tooltip>
                    </template>

                    <InputNumber
                      style="height: 48px;"
                      v-model="addForm.participantPassesQuota"
                      :min="0"
                      :max="availableQuotas"
                    />
                  </InputLabel>
                </div>
                <div class="subexponent__header-add-block-btn">
                  <template v-if="!isAddFormSubmitting">
                    <PrimaryButton
                      :disabled="!isAddFormCompleted"
                      type="submit"
                    >
                      {{ $t("common.addBtn") }}
                    </PrimaryButton>
                  </template>

                  <template v-else>
                    <PrimaryButton disabled>
                      {{ $t("common.addingBtn") }}
                    </PrimaryButton>
                  </template>

                  <p class="subexponent__header-add-block-btn-text">
                    {{ $t("event.subexponent.modal.addHint") }}
                  </p>
                </div>
              </form>
            </div>
          </div>

          <div
            class="subexponent__header-bot subexponent_container"
            v-if="subParticipants.length"
          >
            <SearchField
              :placeholder="$t('event.subexponent.modal.searchPlaceholder')"
              v-model="searchText"
            />

            <div class="subexponent__header-bot-show">
              <LinkIcon
                @click="view = 'table'"
                icon="list-show-grey"
                :bold="true"
                :grey="view !== 'table'"
                :darkBlue="view === 'table'"
              >
                {{ $t("event.subexponent.modal.viewByList") }}
              </LinkIcon>

              <LinkIcon
                @click="view = 'card'"
                icon="card"
                :bold="true"
                :grey="view !== 'card'"
                :darkBlue="view === 'card'"
              >
                {{ $t("event.subexponent.modal.viewByCards") }}
              </LinkIcon>
            </div>
          </div>
        </div>

        <div class="subexponent_container" v-if="subParticipants.length">
          <EventModalSubexponentCard
            v-if="view === 'card'"
            :available-quotas="availableQuotas"
            :subexponents="searchedSubParticipants"
            @onDelete="deleteSubexponentHandler"
            @update-access-level="updateAccessLevel"
            @update-personal-passes-quota="updatePersonalPassesQuota"
            @go-to-subexponent="goToSubexponent"
            :isButtonDisabled="isButtonDisabled"
          />

          <EventModalSubexponentTable
            v-if="view === 'table'"
            :available-quotas="availableQuotas"
            :subexponents="searchedSubParticipants"
            @onDelete="deleteSubexponentHandler"
            @update-personal-passes-quota="updatePersonalPassesQuota"
            @update-access-level="updateAccessLevel"
            @go-to-subexponent="goToSubexponent"
            :isButtonDisabled="isButtonDisabled"
          />
        </div>
      </div>
    </Modal>
  </global-modal>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import apiSubexponent from "@/api/subexponent";
import modalHolder from "@/helpers/modalHolder";
import Modal from "@/components/Modal/Modal.vue";
import {
  LinkIcon,
  SearchField,
  PrimaryButton,
  Tooltip,
  InputLabel,
  InputNumber,
  Popup
} from "@v2/shared/ui/components";
import { i18n } from "@v2/shared/config";
import EventModalSubexponentCard from "./EventModalSubexponentCard.vue";
import EventModalSubexponentTable from "./EventModalSubexponentTable.vue";
import getError from "@/helpers/error";

export default {
  components: {
    Popup,
    Modal,
    LinkIcon,
    SearchField,
    InputLabel,
    InputNumber,
    PrimaryButton,
    EventModalSubexponentCard,
    EventModalSubexponentTable,
    Tooltip
  },

  data() {
    return {
      addForm: {
        companyName: "",
        email: "",
        inn: "",
        participantPassesQuota: 0
      },
      isButtonDisabled: false,
      isAddFormSubmitting: false,
      searchText: "",

      view: "table",
      isAddFormOpen: false,
      subexponentIdToBeDeleted: null,
      subexponentAllToBeDeleted: null,
      hexTokens: {
        F: {
          pattern: /[0-9]/
        }
      }
    };
  },

  computed: {
    ...mapState("event", ["event", "subParticipants"]),
    ...mapState("passes", ["participantPasses", "participantPassesAmount"]),
    ...mapGetters("event", ["eventId", "hasAccessToWrite"]),
    locale() {
      return i18n.locale;
    },
    quoteSubexpPass() {
      return this.event && this.event.quota ? this.event.quota.certificate : 0;
    },

    availableQuotas() {
      return (
        this.quoteSubexpPass -
        this.participantPassesAmount -
        this.subParticipants.reduce((acc, x) => {
          acc += x.quota;
          return acc;
        }, 0)
      );
    },

    isAddFormCompleted() {
      if (i18n.locale === "en") {
        return (
          this.addForm.companyName &&
          this.addForm.email &&
          this.addForm.participantPassesQuota
        );
      }
      return (
        this.addForm.companyName &&
        this.addForm.email &&
        this.addForm.inn &&
        this.addForm.participantPassesQuota
      );
    },
    searchedSubParticipants() {
      return this.subParticipants.filter(item => {
        return (
          (item.company[i18n.locale === "en" ? "name_eng" : "name_rus"] || "")
            .toLowerCase()
            .indexOf(this.searchText.toLowerCase()) !== -1
        );
      });
    }
  },

  methods: {
    deleteSubexponentHandler(id) {
      this.subexponentIdToBeDeleted = id;
    },
    showPopupDeleteAll() {
      this.subexponentAllToBeDeleted = true;
    },
    deleteSubexponentPopupAction() {
      this.deleteSubexponent(this.subexponentIdToBeDeleted);
      this.subexponentIdToBeDeleted = null;
    },

    cancelDeleteSubexponentPopupAction() {
      this.subexponentIdToBeDeleted = null;
    },

    deleteSubexponent(id) {
      apiSubexponent.delete(this.eventId, id).finally(() => {
        this.$store.dispatch("event/fetchSubparticipants", this.eventId);
      });
    },

    async deleteAllsubParticipants() {
      await apiSubexponent.deleteAll(this.eventId);
      this.$store.dispatch("event/fetchSubparticipants", this.eventId);
      this.subexponentAllToBeDeleted = null;
    },
    cancelDeleteAllSubexponentPopupAction() {
      this.subexponentAllToBeDeleted = null;
    },

    addSubexponent() {
      if (!this.isAddFormCompleted) {
        return;
      }

      this.isAddFormSubmitting = true;
      apiSubexponent
        .add(this.eventId, [
          {
            name_rus: this.addForm.companyName,
            name_eng: i18n.locale === "en" ? this.addForm.companyName : "",
            email: this.addForm.email,
            inn: this.addForm.inn,
            certificate: this.addForm.participantPassesQuota
          }
        ])
        .then(response => {
          if (response && response.data)
            this.$store.dispatch("event/fetchSubparticipants", this.eventId);
        })
        .then(() => {
          this.addForm.companyName = "";
          this.addForm.email = "";
          this.addForm.inn = "";
          this.addForm.participantPassesQuota = 0;

          this.isAddFormOpen = false;
        })
        .catch(e => {
          this.$store.dispatch("common/setErrorMessage", getError(e));
        })
        .finally(() => {
          this.isAddFormSubmitting = false;
        });
    },

    updateAccessLevel({ subexponent, accessLevel }) {
      apiSubexponent
        .update(this.eventId, subexponent.id, {
          is_edit: accessLevel === "edit",
          certificate: subexponent.quota
        })
        .then(() =>
          this.$store.dispatch("event/fetchSubparticipants", this.eventId)
        )
        .catch(e => {
          this.$store.dispatch("common/setErrorMessage", getError(e));
        });
    },

    updatePersonalPassesQuota({ subexponent, participantPassQuota }) {
      this.isButtonDisabled = true;
      apiSubexponent
        .update(this.eventId, subexponent.id, {
          is_edit: subexponent.is_edit,
          certificate: participantPassQuota
        })
        .then(() =>
          this.$store.dispatch("event/fetchSubparticipants", this.eventId)
        )
        .catch(e => {
          this.$store.dispatch("common/setErrorMessage", getError(e));
        })
        .finally(() => {
          this.isButtonDisabled = false;
        });
    },

    goToSubexponent(subexponent) {
      modalHolder.popInstance();

      this.$router.push({
        name: "participant",
        params: {
          id: subexponent.id
        }
      });
    },
    onCloseModal() {
      this.isAddFormOpen = false;
      this.$modal.hide("event-modal-subexponent");
    }
  }
};
</script>

<style lang="scss">
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.subexponent {
  padding: 56px 0 64px 0;

  @include respond-down(lg) {
    padding: 48px 0 64px 0;
  }

  @include respond-down(md) {
    padding: 24px 16px 32px 16px;
  }
  &-form__required {
    color: var(--orange, #f6931d);
  }
  &__header {
    &-top {
      display: flex;
      justify-content: space-between;
      align-items: center;

      @include respond-down(md) {
        flex-direction: column;
        align-items: flex-start;
      }

      &-title {
        @include h1();
      }

      &-all {
        margin: 10px 0 0 0;
      }
    }

    &-quotas {
      display: flex;
      align-items: center;
      margin: 32px 0 0 0;

      @include respond-down(md) {
        margin: 24px 0 0 0;
      }

      &-text {
        font-size: 13px;
        line-height: 1.54;
        color: #000000;
        margin: 0 27px 0 0;

        @include respond-down(md) {
          font-size: 12px;
        }

        span {
          font-size: 13px;
          line-height: 1.54;
          color: #6d7179;
          margin-left: 7px;

          @include respond-down(md) {
            font-size: 12px;
          }
        }

        &_mobile {
          margin-left: 0 !important;

          font-size: 13px;
          line-height: 1.54;
          color: #000000 !important;

          @include respond-down(md) {
            display: none;
          }
        }
      }
    }

    &-add {
      margin: 38px 0 0 0;
      display: flex;
      justify-content: space-between;
      align-items: center;

      @include respond-down(md) {
        margin: 36px 0 0 0;
      }

      &-btn {
        &-delete {
          @include button-clean();
          font-size: 12px;
          font-weight: 500;

          line-height: 1.67;

          color: #a3a8b3;
        }
      }

      &-block {
        background-color: rgba(#f9f9f9, 0.5);

        padding: 42px 0 62px 0;
        margin: 36px 0 0 0;
        border-top: 2px solid #f1f1f1;
        border-bottom: 2px solid #f1f1f1;

        @include respond-down(md) {
          margin: 36px -16px 0 -16px;
          padding: 30px 0 32px 0;
        }

        &_container {
          max-width: 780px;
          width: 100%;

          padding: 0 0 0 80px;

          @include respond-down(lg) {
            padding: 0 24px;
            box-sizing: border-box;
          }

          @include respond-down(md) {
            padding: 0 16px;
            box-sizing: border-box;
          }
        }

        &-title {
          @include h2();
          display: flex;
          align-items: center;
          justify-content: space-between;

          .svg-icon {
            width: 16px;
            height: 16px;
            transform: rotate(180deg);
          }
        }

        &-inputs {
          display: flex;

          margin: 38px 0 40px 0;

          @include respond-down(md) {
            margin: 16px 0 24px 0;
            flex-direction: column;
          }

          &-item {
            width: 270px;
            margin: 0 21px 0 0;

            @include respond-down(md) {
              width: 100%;
              margin-bottom: 16px;
            }

            &-number {
              @include respond-down(md) {
                width: 100px;
              }
              .description {
                @include description();
              }
              .svg-icon {
                width: 16px;
                height: 16px;
              }

              @include respond-down(md) {
                .input-number {
                  width: 100px;
                }
              }
            }
          }
        }

        &-btn {
          display: flex;
          align-items: center;

          @include respond-down(md) {
            flex-direction: column;
            align-items: flex-start;
          }

          &-text {
            font-size: 12px;
            color: #6d7179;
            line-height: 1.33;
            max-width: 311px;
            margin: 0 0 0 32px;

            @include respond-down(md) {
              margin: 16px 0 0 0;
            }
          }
        }
      }
    }

    &-bot {
      margin: 26px 0 0 0;

      display: flex;
      justify-content: space-between;
      align-items: center;

      &-show {
        width: 211px;
        display: flex;
        align-items: center;
        justify-content: space-between;

        @include respond-down(md) {
          display: none;
        }
      }
    }
  }

  &_container {
    max-width: 780px;
    width: 100%;

    padding: 0 0 0 80px;

    @include respond-down(lg) {
      padding: 0 24px;
      box-sizing: border-box;
    }

    @include respond-down(md) {
      padding: 0;
    }
  }
}
</style>
